import { Button } from '@civilgrid/ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type CreateExtractAutomationBody, createExtractAutomation } from '~/api/extract';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { MajorHeading } from '../common/Heading';
import { createAutomationConfirmMessage, EXTRACT_ADMIN_STATE_QUERY_KEY } from './common';

type TFormValues = {
  name: string;
  description: string;
  automationId: string;
  webhookUrl: string;
};

export default function CreateExtractAutomation({ activeAutomationName }: { activeAutomationName?: string }) {
  const form = useForm<TFormValues>({
    defaultValues: { name: '', description: '', automationId: '', webhookUrl: '' },
  });
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: CreateExtractAutomationBody) => createExtractAutomation(body),
    onSuccess: (res) => {
      Notify.info({
        title: 'Create Extract Automation',
        message: `Automation "${res.automation.name}" is now active.`,
      });
      form.reset();
      queryClient.invalidateQueries({ queryKey: EXTRACT_ADMIN_STATE_QUERY_KEY });
    },
    onError: () => {
      Notify.error({
        title: 'Create Extract Automation',
        message: 'An error occurred while creating the automation. Check logs for details.',
      });
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = (data) => {
    if (!window.confirm(createAutomationConfirmMessage(data.name.trim(), activeAutomationName))) {
      return;
    }

    mutation.mutate({
      name: data.name,
      description: data.description || undefined,
      automationId: data.automationId,
      webhookUrl: data.webhookUrl,
    });
  };

  const isProcessing = mutation.isPending;

  return (
    <div className="flex flex-col items-start space-y-4">
      <MajorHeading tooltip="Adds an automation and makes it active. The current one is archived.">
        Create Extract Automation
      </MajorHeading>
      <Form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full max-w-md flex-col items-start space-y-4">
        <ControlledInput
          control={form.control}
          name="name"
          label="Name"
          isRequired
          placeholder="Ex: Primary FME Automation"
          data-testid="extract-automation-name-input"
        />
        <ControlledInput
          control={form.control}
          name="description"
          label="Description"
          placeholder="Optional description"
          data-testid="extract-automation-description-input"
        />
        <ControlledInput
          control={form.control}
          name="automationId"
          label="FME Automation ID"
          isRequired
          placeholder="Ex: auto-1234"
          data-testid="extract-automation-id-input"
        />
        <ControlledInput
          control={form.control}
          name="webhookUrl"
          label="Webhook URL"
          isRequired
          placeholder="https://..."
          data-testid="extract-automation-webhook-input"
        />
        <Button type="submit" loading={isProcessing} disabled={isProcessing} data-testid="extract-automation-submit">
          Create Automation
        </Button>
      </Form>
    </div>
  );
}
